import api from '../../services/api';
import FleetPageState from '../../components/FleetPageState';
import PageHeader from '../../components/PageHeader';
// src/pages/Dashboard.tsx - ENHANCED PROFESSIONAL DASHBOARD
import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Button,
  IconButton,
  Alert,
  LinearProgress,
  Fade,
  Chip,
  Paper,
  Divider,
  Avatar,
  Stack,
  useTheme,
  alpha,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Dialog,
  DialogContent,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Speed as SpeedIcon,
  LocalGasStation as FuelIcon,
  DirectionsCar as CarIcon,
  Engineering as EngineIcon,
  TrendingUp,
  TrendingDown,
  Warning as WarningIcon,
  CheckCircleOutline,
  LocalShipping,
  Commute,
  Engineering,
  LocationOn,
  BatteryChargingFull,
  BarChart as BarChartIcon,
} from '@mui/icons-material';

import DevicesIcon from '@mui/icons-material/Devices';


import { formatDateTime } from '../../utils/dateUtils';


import { useQuery } from '@tanstack/react-query';
import { Device } from '../../types';
import { analyticsAPI } from '../../services/api';
import AddDeviceDialog from '../../components/AddDeviceDialog';
import RealDriverSafetyScorecard from '../../components/RealDriverSafetyScorecard';
import RealFleetMileageChart from '../../components/RealFleetMileageChart';
import RealFuelTrendChart from '../../components/RealFuelTrendChart';
import RealEngineHealthWidget from '../../components/RealEngineHealthWidget';
import RealIdleTimeWidget from '../../components/RealIdleTimeWidget';
import { useRealTimeLocation } from '../../hooks/useRealTimeLocation';
import GeofenceAlertsWidget from '../../components/GeofenceAlertsWidget';
import SpeedAnalyticsChart from '../../components/SpeedAnalyticsChart';

const Dashboard: React.FC = () => {
  const theme = useTheme();
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [openAdd, setOpenAdd] = useState(false);
  const [showFirstDevicePrompt, setShowFirstDevicePrompt] = React.useState(false);

  // ✅ React Query - Automatic caching, refetching, and loading states
  const {
    data: dashboardData,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
    dataUpdatedAt
  } = useQuery({
    queryKey: ['dashboard-initial-load', '7d'],
    queryFn: ({ signal }) => analyticsAPI.getDashboardInitialLoad('7d', signal),
    retry: false,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // ✅ Active Alerts Query
  const { data: alertsData, refetch: refetchAlerts } = useQuery({
    queryKey: ['dashboard-alerts'],

    queryFn: async ({ signal }) => (await api.get('/dashboard/active-alerts', { signal })).data,
    retry: false,
    staleTime: 2 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });

  // ✅ Vehicle Performance Query - MOVED TO TOP LEVEL
  const { data: performanceData, isLoading: loadingPerformance, refetch: refetchPerformance } = useQuery({
    queryKey: ['vehicle-performance', '14'],
    queryFn: async ({ signal }) => (await api.get('/dashboard/vehicle-performance', { params: { days: 14 }, signal })).data,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (dashboardData && !selectedDevice && dashboardData.devices?.length > 0) {
      const firstOnlineDevice = dashboardData.devices.find(d => d.status === 'online');
      if (firstOnlineDevice) {
        setSelectedDevice(firstOnlineDevice);
      }
    }
  }, [dashboardData, selectedDevice]);

  // Show "add first device" prompt if company has no devices yet
  useEffect(() => {
    if (dashboardData && !isLoading) {
      const hasDevices = (dashboardData.devices?.length ?? 0) > 0;
      if (!hasDevices) {
        // Only show if not already dismissed this session
        const dismissed = sessionStorage.getItem('firstDevicePromptDismissed');
        if (!dismissed) {
          setShowFirstDevicePrompt(true);
        }
      }
    }
  }, [dashboardData, isLoading]);

  // Real-time location tracking
  const {
    currentLocation,
    isConnected,
    error: locationError,
    isMoving,
    clearError: clearLocationError,
  } = useRealTimeLocation(selectedDevice?.imei);

  const handleRefresh = () => {
    void refetch();
    void refetchAlerts();
    void refetchPerformance();
  };

  if (isLoading || (isError && !dashboardData)) {
    return <FleetPageState page="dashboard" error={isError ? error : undefined} onRetry={handleRefresh} />;
  }

  const {
    devices = [],
    summary = null,
    mileage = null,
    fuel = null,
    engine = null,
    idle = null,
    speed = null
  } = (dashboardData as any) || {};

  // ✅ FIXED: Calculate additional metrics with proper null checks using optional chaining
  const utilizationRate = summary?.fleet?.totalVehicles > 0 ?
    Math.round((summary.fleet.healthyVehicles / summary.fleet.totalVehicles) * 100) : 0;

  const avgSpeedKmh = mileage?.dailyBreakdown?.reduce((acc: number, day: any) =>
    acc + (day.avgSpeed || 0), 0) / (mileage?.dailyBreakdown?.length || 1) || 0;

  return (
    <Fade in timeout={500}>
      <Box>
        {isError && <Alert severity="warning" sx={{ mb: 2 }} action={<Button onClick={handleRefresh}>Retry</Button>}>Couldn’t refresh the dashboard. Showing the last loaded data.</Alert>}
        {/* Enhanced Header with Gradient */}
        <PageHeader title="Fleet Dashboard" description={<>Real-time monitoring and analytics • Last updated: {new Date(dataUpdatedAt).toLocaleTimeString()}</>} icon="dashboard" actions={<Box sx={{ display: 'flex', gap: 2 }}>
              <Tooltip title="Refresh data">
                <IconButton
                  onClick={handleRefresh}
                  disabled={isFetching}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenAdd(true)}
                sx={{
                  bgcolor: 'white',
                  color: 'primary.main',
                  '&:hover': { bgcolor: 'grey.100' },
                  fontWeight: 'bold',
                  boxShadow: 3,
                }}
              >
                Add Device
              </Button>
            </Box>} />


        {/* ✅ NEW: Active Alerts Section */}
        {/* {(() => {
          const alerts = alertsData?.data || [];

          // DEBUG: Log what we're getting
          console.log('🔍 Active Alerts Debug:', {
            hasAlertsData: !!alertsData,
            alertsDataStructure: alertsData,
            alertsArray: alerts,
            alertsCount: alerts.length
          });

          // TEMP: Always show the card for debugging
          return (
            <Card sx={{
              mb: 3,
              bgcolor: alpha(theme.palette.error.main, 0.05),
              borderLeft: `4px solid ${theme.palette.error.main}`,
              boxShadow: 3,
            }}>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'error.main', width: 48, height: 48 }}>
                    <WarningIcon fontSize="large" />
                  </Avatar>
                  <Box flex={1} sx={{ minWidth: 0 }}>
                    <Typography variant="h6" color="error.main" fontWeight="bold">
                      🚨 Active Alerts ({alerts.length})
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {alerts.length === 0 ? 'No alerts found - Check console for debug info' : 'Immediate attention required'}
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                  >
                    View All
                  </Button>
                </Stack>

                {alerts.length === 0 ? (
                  <Alert severity="info">
                    No active alerts. Check browser console (F12) for debug information.
                  </Alert>
                ) : (
                  <Stack spacing={1}>
                    {alerts.slice(0, 5).map((alert: any) => (
                      <Alert
                        key={alert.id}
                        severity={alert.severity}
                        icon={
                          alert.type === 'maintenance' ? <Engineering fontSize="small" /> :
                          alert.type === 'speeding' ? <SpeedIcon fontSize="small" /> :
                          alert.type === 'geofence' ? <LocationOn fontSize="small" /> :
                          <WarningIcon fontSize="small" />
                        }
                        action={
                          <Button size="small" color="inherit">
                            {alert.action}
                          </Button>
                        }
                      >
                        <Typography variant="body2">
                          <strong>{alert.vehicle}</strong>: {alert.message}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDateTime(alert.timestamp, company?.settings?.timezone)}
                        </Typography>
                      </Alert>
                    ))}
                  </Stack>
                )}

                {alerts.length > 5 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                    +{alerts.length - 5} more alerts
                  </Typography>
                )}
              </CardContent>
            </Card>
          );
        })()} */}

        {/* Enhanced Fleet Summary Cards */}




        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 4 }}>
          {/* Total Vehicles */}
          <Grid item xs={12} sm={6} lg={3}>
            <Card sx={{
              height: '100%',
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
              border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: { xs: 40, sm: 48 }, height: { xs: 40, sm: 48 }, flexShrink: 0 }}>
                    <CarIcon fontSize="large" />
                  </Avatar>
                  <Box flex={1} sx={{ minWidth: 0 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Total Vehicles
                    </Typography>
                    <Typography variant="h3" sx={{ fontSize: { xs: 32, sm: 40 }, overflowWrap: 'anywhere' }} fontWeight="bold">
                      {devices.length}
                    </Typography>
                    <Chip
                      label="Active in fleet"
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ mt: 1 }}
                    />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

         {/* Healthy Vehicles */}
<Grid item xs={12} sm={6} lg={3}>
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Avatar sx={{ bgcolor: 'success.main', width: { xs: 40, sm: 48 }, height: { xs: 40, sm: 48 }, flexShrink: 0 }}>
          <CheckCircleIcon fontSize="large" />
        </Avatar>
        <Box flex={1} sx={{ minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Healthy Vehicles
          </Typography>
          <Typography variant="h3" sx={{ fontSize: { xs: 32, sm: 40 }, overflowWrap: 'anywhere' }} fontWeight="bold" color="success.main">
            {engine?.filter((e: any) => e.status === 'healthy').length || 0}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={summary?.fleet?.activeVehicles > 0
              ? Math.round((engine?.filter((e: any) => e.status === 'healthy').length || 0) / summary.fleet.activeVehicles * 100)
              : 0}
            color="success"
            sx={{ mt: 1, mb: 1, height: 6, borderRadius: 3 }}
          />
          <Typography variant="caption" color="text.secondary">
            {summary?.fleet?.activeVehicles > 0
              ? Math.round((engine?.filter((e: any) => e.status === 'healthy').length || 0) / summary.fleet.activeVehicles * 100)
              : 0}% of active fleet
          </Typography>
          {summary?.fleet?.inactiveVehicles > 0 && (
            <Chip
              size="small"
              icon={<WarningIcon />}
              label={`${summary.fleet.inactiveVehicles} inactive`}
              color="warning"
              variant="outlined"
              sx={{ mt: 0.5, fontSize: '0.7rem' }}
            />
          )}
        </Box>
      </Stack>
    </CardContent>
  </Card>
</Grid>

          {/* Total Mileage */}
          <Grid item xs={12} sm={6} lg={3}>
            <Card sx={{
              height: '100%',
              background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.info.light, 0.05)} 100%)`,
              border: `2px solid ${alpha(theme.palette.info.main, 0.1)}`,
            }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: 'info.main', width: { xs: 40, sm: 48 }, height: { xs: 40, sm: 48 }, flexShrink: 0 }}>
                    <SpeedIcon fontSize="large" />
                  </Avatar>
                  <Box flex={1} sx={{ minWidth: 0 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Total Mileage (7d)
                    </Typography>
                    <Typography variant="h3" sx={{ fontSize: { xs: 32, sm: 40 }, overflowWrap: 'anywhere' }} fontWeight="bold" color="info.main">
                      {Math.round(mileage?.fleetDaily?.slice(-7)?.reduce((sum: number, day: any) => sum + (day.totalMileage || 0), 0) || 0)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      km traveled
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        Avg per vehicle
                      </Typography>
                      <Typography variant="caption" fontWeight="bold">
                        {devices.length > 0 ? Math.round((mileage?.fleetDaily?.slice(-7)?.reduce((sum: number, day: any) => sum + (day.totalMileage || 0), 0) || 0) / devices.length) : 0} km
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Avg speed
                      </Typography>
                      <Typography variant="caption" fontWeight="bold">
                      {(dashboardData as any)?.speed?.summary?.avgFleetSpeed || 0} km/h
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Total Trips & Efficiency */}
          <Grid item xs={12} sm={6} lg={3}>
            <Card sx={{
              height: '100%',
              background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.light, 0.05)} 100%)`,
              border: `2px solid ${alpha(theme.palette.success.main, 0.1)}`,
            }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: 'success.main', width: { xs: 40, sm: 48 }, height: { xs: 40, sm: 48 }, flexShrink: 0 }}>
                    <CarIcon fontSize="large" />
                  </Avatar>
                  <Box flex={1} sx={{ minWidth: 0 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Total Trips (7d)
                    </Typography>
                    <Typography variant="h3" sx={{ fontSize: { xs: 32, sm: 40 }, overflowWrap: 'anywhere' }} fontWeight="bold" color="success.main">
                      {summary?.efficiency?.totalTrips || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      trips completed
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        Idle time
                      </Typography>
                      <Chip
                        label={`${Math.round((idle?.reduce((sum: number, d: any) => sum + (d.totalIdleMinutes || 0), 0) || 0) / 60)}h`}
                        size="small"
                        color="warning"
                        sx={{ fontWeight: 'bold' }}
                      />
                    </Box>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Main Dashboard Widgets */}
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {/* Engine Health */}
          <Grid item xs={12} md={6} xl={4}>
            <Card sx={{
              height: { xs: 390, sm: 420 },
              display: 'flex', flexDirection: 'column', minWidth: 0,
              boxShadow: 3,
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 6,
              }
            }}>
              <Box sx={{
                p: { xs: 2, sm: 2.5 },
                flexShrink: 0, flexWrap: 'wrap', gap: 1,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ bgcolor: 'success.main', width: 40, height: 40 }}>
                    <EngineIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      Engine Health
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Real-time diagnostics
                    </Typography>
                  </Box>
                </Box>
                <Chip
                  label="Live"
                  size="small"
                  color="success"
                  icon={<CheckCircleIcon fontSize="small" />}
                />
              </Box>
              <Box sx={{ p: 2, flex: 1, minHeight: 0, overflow: 'auto' }}>
                <RealEngineHealthWidget data={engine} />
              </Box>
            </Card>
          </Grid>

          {/* Fleet Mileage Chart */}
          <Grid item xs={12} md={6} xl={4}>
            <Card sx={{
              height: { xs: 390, sm: 420 },
              display: 'flex', flexDirection: 'column', minWidth: 0,
              boxShadow: 3,
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 6,
              }
            }}>
              <Box sx={{
                p: { xs: 2, sm: 2.5 },
                flexShrink: 0, flexWrap: 'wrap', gap: 1,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ bgcolor: 'info.main', width: 40, height: 40 }}>
                    <BarChartIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      Fleet Mileage
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Last 7 days trend
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ p: 2, flex: 1, minHeight: 0, overflow: 'auto' }}>
                <RealFleetMileageChart data={mileage} />
              </Box>
            </Card>
          </Grid>

          {/* Fuel Consumption */}
          <Grid item xs={12} md={6} xl={4}>
            <Card sx={{
              height: { xs: 390, sm: 420 },
              display: 'flex', flexDirection: 'column', minWidth: 0,
              boxShadow: 3,
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 6,
              }
            }}>
              <Box sx={{
                p: { xs: 2, sm: 2.5 },
                flexShrink: 0, flexWrap: 'wrap', gap: 1,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ bgcolor: 'error.main', width: 40, height: 40 }}>
                    <FuelIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      Fuel Consumption
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Usage trend analysis
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ p: 2, flex: 1, minHeight: 0, overflow: 'auto' }}>
                <RealFuelTrendChart data={fuel?.byDate || []} />
              </Box>
            </Card>
          </Grid>

          {/* Speed Analytics - NEW */}
          <Grid item xs={12} lg={6}>
            <Card sx={{
              height: { xs: 390, sm: 420 },
              display: 'flex', flexDirection: 'column', minWidth: 0,
              boxShadow: 3,
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 6,
              }
            }}>
              <Box sx={{
                p: { xs: 2, sm: 2.5 },
                flexShrink: 0, flexWrap: 'wrap', gap: 1,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ bgcolor: 'error.main', width: 40, height: 40 }}>
                    <SpeedIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      Speed Analytics
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Fleet speed trends and violations
                    </Typography>
                  </Box>
                </Box>
                {(dashboardData as any)?.speed?.summary?.totalViolations > 0 && (
                  <Chip
                    icon={<WarningIcon fontSize="small" />}
                    label={`${(dashboardData as any).speed.summary.totalViolations} violations`}
                    size="small"
                    color="warning"
                  />
                )}
              </Box>
              <Box sx={{ p: 2, flex: 1, minHeight: 0, overflow: 'auto' }}>
                <SpeedAnalyticsChart data={(dashboardData as any)?.speed} />
              </Box>
            </Card>
          </Grid>


          {/* Idle Time Analysis */}
          {/* <Grid item xs={12} lg={6}>
            <Card sx={{
              height: { xs: 390, sm: 420 },
              display: 'flex', flexDirection: 'column', minWidth: 0,
              boxShadow: 3,
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 6,
              }
            }}>
              <Box sx={{
                p: { xs: 2, sm: 2.5 },
                flexShrink: 0, flexWrap: 'wrap', gap: 1,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ bgcolor: 'warning.main', width: 40, height: 40 }}>
                    <TimelapseIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      Idle Time Analysis
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Efficiency metrics
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ p: 2, flex: 1, minHeight: 0, overflow: 'auto' }}>
                <RealIdleTimeWidget data={idle} />
              </Box>
            </Card>
          </Grid> */}

          {/* Driver Safety Scorecard */}
          <Grid item xs={12} lg={6}>
            <Card sx={{
              height: { xs: 390, sm: 420 },
              display: 'flex', flexDirection: 'column', minWidth: 0,
              boxShadow: 3,
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 6,
              }
            }}>
              <Box sx={{
                p: { xs: 2, sm: 2.5 },
                flexShrink: 0, flexWrap: 'wrap', gap: 1,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ bgcolor: 'secondary.main', width: 40, height: 40 }}>
                    <Commute />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      Driver Safety
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Performance scorecard
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ p: 2, flex: 1, minHeight: 0, overflow: 'auto' }}>
                <RealDriverSafetyScorecard engineHealthData={engine} />
              </Box>
            </Card>
          </Grid>

          {/* Geofence Alerts - Full Width */}
          <Grid item xs={12}>
            <Card sx={{
              boxShadow: 3,
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 6,
              }
            }}>
              <Box sx={{
                p: { xs: 2, sm: 2.5 },
                flexShrink: 0, flexWrap: 'wrap', gap: 1,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                    <LocationOn />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      Speed & Geofence Violations
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Recent speed and zone events
                    </Typography>
                  </Box>
                </Box>
                <Chip
                  label="Last 24 hours"
                  size="small"
                  variant="outlined"
                />
              </Box>
              <Box sx={{ p: 2 }}>
                <GeofenceAlertsWidget />
              </Box>
            </Card>
          </Grid>
        </Grid>


        {/* Add Device Dialog */}
        <AddDeviceDialog
          open={openAdd}
          onClose={() => setOpenAdd(false)}
          onAdded={(created: Device) => {
            if (created.status === 'online') {
              setSelectedDevice(created);
            }
            refetch();
          }}
        />

        {/* ── First device prompt ── */}
        <Dialog
          open={showFirstDevicePrompt && !openAdd}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogContent sx={{ textAlign: 'center', pt: 4, pb: 2 }}>
            <Box sx={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(56,189,248,0.12)', display: 'grid', placeItems: 'center', mx: 'auto', mb: 2 }}>
              <DevicesIcon sx={{ color: '#38bdf8', fontSize: 32 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>Add your first device</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Your fleet has no devices yet. Add your first GPS device to start tracking your vehicles in real time.
            </Typography>
            <Stack spacing={1.5}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => { setShowFirstDevicePrompt(false); setOpenAdd(true); }}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800 }}
              >
                Add First Device
              </Button>
              <Button
                fullWidth
                variant="text"
                onClick={() => {
                  sessionStorage.setItem('firstDevicePromptDismissed', '1');
                  setShowFirstDevicePrompt(false);
                }}
                sx={{ borderRadius: 2, textTransform: 'none', color: 'text.secondary' }}
              >
                Remind me later
              </Button>
            </Stack>
          </DialogContent>
        </Dialog>

      </Box>
    </Fade>
  );
};

export default Dashboard;
